/*!

=========================================================
* Argon Dashboard React - v1.2.3
=========================================================

* Product Page: https://www.creative-tim.com/product/argon-dashboard-react
* Copyright 2023 Creative Tim (https://www.creative-tim.com)
* Licensed under MIT (https://github.com/creativetimofficial/argon-dashboard-react/blob/master/LICENSE.md)

* Coded by Creative Tim

=========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.

*/

/* global chrome */
import ReactDOM from 'react-dom/client';
import { BrowserRouter, MemoryRouter, Navigate, Route, Routes } from 'react-router-dom';
import { initAppcues } from './appcues/index.js';

import '@fortawesome/fontawesome-free/css/all.min.css';
import 'assets/plugins/nucleo/css/nucleo.css';
import 'assets/scss/argon-dashboard-react.scss';
import 'assets/css/auth-redesign.css';

import AuthLayout from 'layouts/Auth.js';
import UserLayout from 'layouts/User.js';

import { GlobalContext, GlobalProvider } from 'api/GlobalContext.js';
import AutoTrigger from 'components/AutoTrigger.js';
import NotesUploadBridge from 'components/NotesUploadBridge.js';
import { useContext, useEffect, useState } from 'react';
import AppSplash from 'components/Workspace/AppSplash.js';

import { getCXVersion, setupComplianceRailBridge } from 'EMR_Integration/react_webInjector.ts';
import { adoptPlaygroundSession } from 'api/playgroundSession.js';

const isEdgeExtension =
  window.location.protocol === 'chrome-extension:' &&
  /Edg\//.test(window.navigator.userAgent);
// Edge remounts the side panel when BrowserRouter changes its URL.
const Router = isEdgeExtension ? MemoryRouter : BrowserRouter;

const root = ReactDOM.createRoot(document.getElementById('root'));
const EXTENSION_SESSION_KEY = 'comprehendpt_extension_session';
const PERSISTENT_SESSION_KEY = 'comprehendpt_note_capture_session';

const App = () => {
  const { apiClient } = useContext(GlobalContext);

  useEffect(() => {
    if (!window.location.pathname.startsWith('/auth')) {
      initAppcues();
    }
  }, []);

  // The embed's compliance rail grades and opens visits through this panel.
  useEffect(() => setupComplianceRailBridge(apiClient), [apiClient]);

  useEffect(() => {
    const update = () => updateExtensionSession(apiClient, apiClient?.features);
    const clearFeatures = () => updateExtensionSession(apiClient);
    const clearFeaturesAfterCredentialsChange = () => queueMicrotask(clearFeatures);
    const originalReset = apiClient.reset;
    const originalSetSessionKey = apiClient.setSessionKey;
    const originalSetSessionEmail = apiClient.setSessionEmail;

    if (typeof originalReset === 'function') {
      apiClient.reset = (...args) => {
        clearExtensionSession();
        return originalReset.apply(apiClient, args);
      };
    }
    if (typeof originalSetSessionKey === 'function') {
      apiClient.setSessionKey = (...args) => {
        const result = originalSetSessionKey.apply(apiClient, args);
        clearFeaturesAfterCredentialsChange();
        return result;
      };
    }
    if (typeof originalSetSessionEmail === 'function') {
      apiClient.setSessionEmail = (...args) => {
        const result = originalSetSessionEmail.apply(apiClient, args);
        clearFeaturesAfterCredentialsChange();
        return result;
      };
    }

    apiClient.onProfileUpdated?.(update);
    apiClient.onBackendUpdated?.(clearFeatures);
    update();

    return () => {
      if (typeof originalReset === 'function') {
        apiClient.reset = originalReset;
      }
      if (typeof originalSetSessionKey === 'function') {
        apiClient.setSessionKey = originalSetSessionKey;
      }
      if (typeof originalSetSessionEmail === 'function') {
        apiClient.setSessionEmail = originalSetSessionEmail;
      }
      apiClient.offProfileUpdated?.(update);
      apiClient.offBackendUpdated?.(clearFeatures);
    };
  }, [apiClient]);

  var navigationDestination = '/User/Home';
  if (window.location.hash === '#micRequest') {
    navigationDestination = '/auth/micRequest';
  }

  // Check for comprehend trigger in URL parameters
  const urlParams = new URLSearchParams(window.location.search);
  const trigger = urlParams.get('trigger');
  const conversationID = urlParams.get('conversationID');
  const tab = urlParams.get('tab');
  // The tab the visit should open on, when the opener (the compliance rail)
  // asked for one. Read by ConversationDetailsCX from location.state.
  let navigationState;

  if (trigger === 'comprehend' && conversationID) {
    // Navigate to conversation details page
    navigationDestination = `/User/conversations/details#${conversationID}`;
    // Any rail button: a built-in, a connected app ('app:<id>'), or Apps.
    if (
      tab &&
      (['note', 'chat', 'compliance', 'coding', 'apps'].includes(tab) ||
        /^app:\S{1,200}$/.test(tab))
    ) {
      navigationState = { landingTab: tab };
    }
  } else {
    console.log('🧠 No comprehend trigger detected in URL');
  }

  // Opened straight onto a connected app (its rail button): cover the boot
  // (Home → the visit → the app's frame) with the co-branded splash until the
  // app is on screen. Read once: the redirect below drops the query string.
  const [splashAppId] = useState(() =>
    trigger === 'comprehend' && conversationID && /^app:\S{1,200}$/.test(tab || '') ? tab.slice(4) : null
  );

  return (
    <>
      {splashAppId && <AppSplash appId={splashAppId} />}
      <AutoTrigger />
      <Routes>
        <Route path="/User/*" element={<UserLayout />} />
        <Route path="/auth/*" element={<AuthLayout />} />
        <Route
          path="*"
          element={
            <Navigate to={navigationDestination} replace state={navigationState} />
          }
        />
      </Routes>
    </>
  );
};

function updateExtensionSession(apiClient, features = {}) {
  const storage = getExtensionSessionStorage();
  if (!storage) return;

  const sessionKey = apiClient?.sessionKey;
  const sessionEmail = apiClient?.sessionEmail;

  if (
    !sessionEmail ||
    sessionKey === null ||
    sessionKey === undefined ||
    !Number.isInteger(Number(sessionKey))
  ) {
    clearExtensionSession();
    return;
  }

  const extensionSession = {
    sessionKey,
    sessionEmail,
    backendBaseUrl: apiClient?.apiClient?.defaults?.baseURL,
    features
  };

  storage.set({
    [EXTENSION_SESSION_KEY]: extensionSession
  });
  // Mirror to local so the background worker can still act for the user after a
  // browser restart. Free Compliance's score bubble assumes only that the user
  // logged in at least once, and chrome.storage.session dies with the browser.
  // Persist only credentials, not feature flags.
  getPersistentCaptureStorage()?.set({
    [PERSISTENT_SESSION_KEY]: {
      sessionKey,
      sessionEmail,
      backendBaseUrl: extensionSession.backendBaseUrl
    }
  });
}

function clearExtensionSession() {
  getExtensionSessionStorage()?.remove(EXTENSION_SESSION_KEY);
  getPersistentCaptureStorage()?.remove(PERSISTENT_SESSION_KEY);
}

function getExtensionSessionStorage() {
  if (
    typeof chrome !== 'undefined' &&
      chrome.runtime?.id &&
      chrome.storage?.session
  ) {
    return chrome.storage.session;
  }
  return null;
}

function getPersistentCaptureStorage() {
  if (
    typeof chrome !== 'undefined' &&
      chrome.runtime?.id &&
      chrome.storage?.local
  ) {
    return chrome.storage.local;
  }
  return null;
}

// Hidden hand-off tab (?adoptPlaygroundSession=1): background.js opens this
// once so the Playground's sign-in gets adopted; GlobalProvider does the
// adopting on every boot, this tab just needs to run it and close itself.
const adoptOnly =
  new URLSearchParams(window.location.search).get('adoptPlaygroundSession') === '1';
if (adoptOnly) {
  adoptPlaygroundSession()
    .catch(() => false)
    .then(() => {
      try {
        chrome.tabs.getCurrent((tab) => {
          if (tab && typeof tab.id === 'number') chrome.tabs.remove(tab.id);
        });
      } catch (e) {
        window.close();
      }
    });
} else {
  root.render(
    <Router>
      <GlobalProvider>
        <NotesUploadBridge />
        <App />
      </GlobalProvider>
      <div style={{ display: 'none' }} id="APP_VERSION">
        {getCXVersion()}
      </div>
    </Router>
  );
}

// MinimalFloatingUI auth: Uses direct extension context check via background script
// No startup syncing needed

// Message listener for background script communication
if (typeof chrome !== 'undefined' && chrome.runtime) {
  chrome.runtime.onMessage.addListener((message, sender, sendResponse) => {
    // Handle simple credential requests from localStorage
    if (message.type === 'GET_CREDENTIALS_FROM_LOCALSTORAGE') {
      try {
        // STEP 3: Get credentials from CX localStorage
        const sessionEmail = localStorage.getItem('sessionEmail');
        const sessionKey = localStorage.getItem('sessionKey');

        // Check if user is authenticated
        const isAuthenticated = !!(
          sessionEmail &&
          sessionKey &&
          !isNaN(parseInt(sessionKey))
        );

        sendResponse({
          success: true,
          isAuthenticated: isAuthenticated,
          sessionEmail: sessionEmail,
          sessionKey: sessionKey,
          source: 'cx_localStorage'
        });

        return true;
      } catch (error) {
        sendResponse({
          success: false,
          isAuthenticated: false,
          error: error.message
        });
        return true;
      }
    }
  });
}
// Legacy window message handler removed - now using chrome.runtime.sendMessage for better reliability
